/**
 * Stage IG-P0:Instagram モジュールの表示ユーティリティ。
 *
 * 時刻の基本形式は既存の utils/dateTime.formatJstDateTime(Asia/Tokyo 固定)を再利用し、
 * ここには「一覧で使う相対表記」など Instagram 固有の見せ方だけを置く。
 */

import { formatJstDateTime } from "../../../utils/dateTime";

/** 数値の 3 桁区切り。null は "—"(設計書 §4.5:取得できない値は偽造しない)。 */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return value.toLocaleString("ja-JP");
}

/** 12486 → "1.2万" のような短縮表記(KPI カードの補助行用)。 */
export function formatCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  if (Math.abs(value) < 10000) return value.toLocaleString("ja-JP");
  return `${(value / 10000).toFixed(1)}万`;
}

/** パーセント表記。null は "—"。 */
export function formatPercent(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${value.toFixed(digits)}%`;
}

/** 増減の符号付き表記。 */
export function formatDelta(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toLocaleString("ja-JP")}`;
}

/**
 * 一覧向けの相対時刻。
 * 今日 → "13:42"、昨日 → "昨日"、今年 → "7/29"、それ以前 → "2025/7/29"。
 * 会話一覧やコメント一覧の右端に置く 11px の表記用。
 */
export function formatRelativeTime(value: string | null | undefined): string {
  if (!value) return "—";
  const raw = String(value).trim();
  if (!raw) return "—";
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(raw);
  const d = new Date(hasTz ? raw : `${raw}Z`);
  if (Number.isNaN(d.getTime())) return "—";

  // JST での日付境界で比較する
  const toJstParts = (date: Date) => {
    const s = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Tokyo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
    const [y, m, day] = s.split("-").map(Number);
    return { y, m, day };
  };

  const target = toJstParts(d);
  const today = toJstParts(new Date());

  const time = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Tokyo",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);

  if (target.y === today.y && target.m === today.m && target.day === today.day) {
    return time;
  }

  const yesterday = new Date(Date.now() - 86400000);
  const y = toJstParts(yesterday);
  if (target.y === y.y && target.m === y.m && target.day === y.day) {
    return "昨日";
  }

  if (target.y === today.y) return `${target.m}/${target.day}`;
  return `${target.y}/${target.m}/${target.day}`;
}

/** メッセージ区切り用の日付見出し。 */
export function formatDateHeading(value: string | null | undefined): string {
  if (!value) return "";
  const raw = String(value).trim();
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(raw);
  const d = new Date(hasTz ? raw : `${raw}Z`);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    month: "long",
    day: "numeric",
    weekday: "short",
  }).format(d);
}

/** 時刻のみ(メッセージバブルの下に出す)。 */
export function formatTimeOnly(value: string | null | undefined): string {
  if (!value) return "";
  const raw = String(value).trim();
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(raw);
  const d = new Date(hasTz ? raw : `${raw}Z`);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Tokyo",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

/** 予定投稿など「日時をきちんと出す」箇所は既存 helper に委譲する。 */
export const formatFullDateTime = formatJstDateTime;

/** アバターのイニシャル(Sidebar の getInitial と同じ考え方)。 */
export function getInitial(name: string | null | undefined): string {
  const s = (name ?? "").trim();
  return s ? s.charAt(0) : "?";
}
